import { LitElement, html, svg, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin, store } from '../store.js';
import { api } from '../api.js';
import { apiParams } from '../lib/filters.js';
import { withFormats } from '../lib/table.js';
import {
  LEVER_KINDS,
  LEVER_KIND_LABELS,
  LEVER_KIND_NOTES,
  SENSITIVITY_MEASURES,
  SENSITIVITY_ORDERS,
  sensitivityColumns,
  sensitivityModel,
  sensitivityRows,
  sensitivitySummary,
  tornadoDomain,
  tornadoLayout,
  tornadoTicks,
} from '../lib/sensitivity.js';
import { hours as formatHours, percent, longDate, count, shortDate } from '../lib/format.js';

/** The shared filters this view reads. The window is the run's, so `from` and `weeks` are not. */
const PARAM_KEYS = Object.freeze(['run_id', 'pipeline']);

/** The chart's own coordinate space. Fixed, so it scales with the card and nothing jumps. */
const PLOT = Object.freeze({
  width: 760,
  gutter: 260,
  rowHeight: 26,
  barHeight: 14,
  top: 26,
  minRows: 3,
  label: 34,
});

const MEASURE_LABELS = { utilization: 'Peak', over_hours: 'Hours over' };
const ORDER_LABELS = { span: 'Span', hours: 'Hours moved', name: 'Name', kind: 'Kind' };
/** The floors the "hide under" control offers, in points of peak utilization. */
const FLOORS = [0, 0.01, 0.05, 0.1];

const plural = (n, one, many = `${one}s`) => `${count(n)} ${n === 1 ? one : many}`;
const exact = (v) => formatHours(v, { exact: true });
const signed = (v, format) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${format(Math.abs(v))}`;

/**
 * The sensitivity tornado (Design Review V-12; PRD 10.9; Phase 47): which single deal or
 * assumption moves the peak the most.
 *
 * It sits above the scenario list because it is the answer to the question that page otherwise
 * leaves to guesswork — *which* what-if is worth building. Every bar is a scenario a planner
 * could construct; the chart says which one would change the number they care about, and by how
 * much, before any of them is built.
 *
 * A bar runs from the base line to the peak that lever would produce, so its length is the move
 * and its side is the direction: left relieves the peak, right makes it worse. The base line is
 * the dashboard's own peak — the same `pickPeak` picks both — so the centre of this chart and the
 * KPI tile on the home page are the same number by construction.
 *
 * Every figure comes from `GET /forecast/sensitivity` and every position from `lib/sensitivity.js`,
 * which the CSV export imports too. This component formats and computes no peaks.
 */
class SensitivityTornado extends StoreMixin(LitElement) {
  static properties = {
    _data: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _measure: { state: true },
    _order: { state: true },
    _kinds: { state: true },
    _floor: { state: true },
    _q: { state: true },
    _hover: { state: true },
    _pinned: { state: true },
    _tableOpen: { state: true },
  };

  constructor() {
    super();
    this._data = null;
    this._loading = false;
    this._error = null;
    this._measure = 'utilization';
    this._order = 'span';
    this._kinds = LEVER_KINDS.filter((k) => k !== 'base');
    this._floor = 0;
    this._q = '';
    this._hover = null;
    this._pinned = null;
    this._tableOpen = false;
    this._requestKey = null;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
        min-width: 0;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
        min-width: 0;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 12px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
        max-width: 72ch;
      }
      .kpis {
        margin-left: auto;
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
      }
      .kpi {
        text-align: right;
      }
      .kpi .n {
        font-size: 18px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
      }
      .kpi .l {
        font-size: 11px;
        color: var(--ink-muted);
      }
      .controls {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 16px;
        margin-bottom: 8px;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .segmented {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .segmented .group {
        display: inline-flex;
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        background: var(--control-bg);
        padding: 2px;
      }
      .segmented .group button {
        font: inherit;
        border: 0;
        background: none;
        border-radius: var(--radius-pill);
        padding: 3px 10px;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--ink-muted);
        cursor: pointer;
      }
      .segmented .group button[aria-pressed='true'] {
        color: var(--surface);
        background: var(--ink-strong);
      }
      .field {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .field select,
      .field input {
        font: inherit;
        font-size: 12.5px;
        color: var(--ink);
        background: var(--control-bg);
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        padding: 3px 9px;
      }
      button.link {
        font: inherit;
        font-size: 12.5px;
        border: 0;
        background: none;
        color: var(--brand);
        cursor: pointer;
        padding: 0;
        text-decoration: underline;
      }
      svg.plot {
        display: block;
        width: 100%;
        height: auto;
        overflow: visible;
      }
      .bar {
        cursor: pointer;
        transition: opacity 120ms ease-out;
      }
      /* Relief and risk are the two readings, and they are never the same colour: the reserved
         status hues say which, so a reader who cannot see the side still reads the sign. */
      .bar[data-relieves='true'] {
        fill: var(--status-healthy);
      }
      .bar[data-relieves='false'] {
        fill: var(--status-overloaded);
      }
      .row[data-dim='true'] .bar,
      .row[data-dim='true'] text {
        opacity: 0.22;
      }
      .base-line {
        stroke: var(--ink-strong);
        stroke-width: 1.5;
      }
      .grid {
        stroke: var(--border-soft);
        stroke-width: 1;
      }
      text {
        font-size: 11.5px;
        fill: var(--ink);
      }
      text.value {
        font-size: 10.5px;
        fill: var(--ink-muted);
        font-variant-numeric: tabular-nums;
      }
      text.axis {
        font-size: 10.5px;
        fill: var(--ink-muted);
        font-variant-numeric: tabular-nums;
      }
      text.base-label {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        fill: var(--ink-muted);
      }
      .legends {
        display: flex;
        flex-wrap: wrap;
        gap: 6px 22px;
        margin: 10px 0 2px;
      }
      ul.legend {
        display: flex;
        flex-wrap: wrap;
        gap: 4px 14px;
        list-style: none;
        margin: 0;
        padding: 0;
        font-size: 11.5px;
        color: var(--ink-muted);
      }
      ul.legend li {
        display: inline-flex;
        align-items: center;
        gap: 5px;
      }
      .sw {
        display: inline-block;
        width: 11px;
        height: 11px;
        border-radius: 2px;
        border: 1px solid var(--border-soft);
      }
      p.note {
        margin: 8px 0 0;
        font-size: 12px;
        line-height: 1.5;
        color: var(--ink-muted);
        max-width: 92ch;
      }
      .state {
        padding: 22px 0;
        color: var(--ink-muted);
        font-size: 13px;
      }
      .state.error {
        color: var(--status-overloaded);
      }
      details.table {
        margin-top: 14px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      data-table {
        display: block;
        margin-top: 10px;
      }
      @media (max-width: 720px) {
        .card {
          padding: 16px 14px 14px;
        }
      }
    `,
  ];

  // -------------------------------------------------------------------------------------------
  // Data

  connectedCallback() {
    super.connectedCallback();
    this._unsubscribeFetch = store.subscribe(() => this._maybeFetch());
    this._maybeFetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeFetch?.();
  }

  _params() {
    const { run } = this.appState;
    if (!run) return null;
    return apiParams(this.appState.filters, PARAM_KEYS);
  }

  async _maybeFetch() {
    const params = this._params();
    const key = params ? JSON.stringify(params) : null;
    if (key === this._requestKey) return;
    this._requestKey = key;
    if (!params) {
      this._data = null;
      return;
    }
    this._loading = true;
    this._error = null;
    try {
      const data = await api.get('/forecast/sensitivity', params);
      if (this._requestKey !== key) return; // a newer request has taken over
      this._data = data;
    } catch (err) {
      if (this._requestKey !== key) return;
      this._error = err.message;
      this._data = null;
    } finally {
      if (this._requestKey === key) this._loading = false;
    }
  }

  // -------------------------------------------------------------------------------------------
  // Derived

  get _model() {
    if (!this._data) return null;
    const key = `${this._measure}|${this._order}|${this._kinds.join(',')}|${this._floor}|${this._q}`;
    if (this._modelFor !== this._data || this._modelKey !== key) {
      this._model_ = sensitivityModel(this._data, {
        measure: this._measure,
        order: this._order,
        kinds: this._kinds,
        minDelta: this._floor,
        q: this._q,
      });
      this._modelFor = this._data;
      this._modelKey = key;
    }
    return this._model_;
  }

  get _focus() {
    return this._hover ?? this._pinned;
  }

  _pin(key) {
    this._pinned = this._pinned === key ? null : key;
  }

  _fmt(value) {
    return this._measure === 'over_hours' ? exact(value) : percent(value);
  }

  _show(event, title, rows, footer) {
    const tooltip = this.renderRoot.querySelector('chart-tooltip');
    if (!tooltip) return;
    tooltip.show({
      x: event.clientX ?? 0,
      y: event.clientY ?? 0,
      title,
      rows: rows.filter(Boolean),
      footer: footer || '',
    });
  }

  _hide() {
    this.renderRoot.querySelector('chart-tooltip')?.hide();
  }

  _showLever(event, model, lever) {
    const detail = lever.detail ?? {};
    this._show(
      event,
      lever.label,
      [
        { label: 'Peak with it pulled', value: this._fmt(lever.value) },
        {
          label: 'Change from the base',
          value: signed(lever.delta, (v) => this._fmt(v)),
        },
        { label: 'Peak week', value: shortDate(lever.peak_week_start) },
        { label: 'Hours it moves', value: signed(lever.hours_moved, exact) },
        lever.hours_moved_peak_week !== 0
          ? {
              label: 'In the base peak week',
              value: signed(lever.hours_moved_peak_week, exact),
            }
          : null,
        { label: 'Weeks over after', value: count(lever.weeks_over) },
        detail.client ? { label: 'Client', value: detail.client } : null,
        detail.stage_name ? { label: 'Stage', value: detail.stage_name } : null,
        detail.probability_used !== undefined && detail.probability_used !== null
          ? { label: 'Weighted at', value: percent(detail.probability_used) }
          : null,
        detail.deals ? { label: 'Deals', value: count(detail.deals) } : null,
        detail.weekly_hours ? { label: 'Weekly hours', value: exact(detail.weekly_hours) } : null,
      ],
      LEVER_KIND_NOTES[lever.lever_kind] ?? '',
    );
  }

  // -------------------------------------------------------------------------------------------
  // The chart

  _plot(model) {
    const rows = Math.max(PLOT.minRows, model.levers.length);
    const inner = PLOT.width - PLOT.gutter - 70;
    const domain = tornadoDomain(model);
    const layout = tornadoLayout(model, {
      width: inner,
      rowHeight: PLOT.rowHeight,
      barHeight: PLOT.barHeight,
      top: PLOT.top,
      domain,
    });
    const ticks = tornadoTicks(domain, { percent: this._measure !== 'over_hours' });
    const height = PLOT.top + rows * PLOT.rowHeight + 26;
    const focus = this._focus;

    return svg`
      <svg
        class="plot"
        viewBox="0 0 ${PLOT.width} ${height}"
        role="img"
        aria-label=${`${plural(layout.bars.length, 'lever')} against a peak of ${this._fmt(
          model.base.value,
        )}`}
        @pointerleave=${() => {
          this._hover = null;
          this._hide();
        }}
      >
        <g transform=${`translate(${PLOT.gutter} 0)`}>
          ${ticks.map(
            (t) => svg`<g>
              <line
                class="grid"
                x1=${layout.scale(t)}
                x2=${layout.scale(t)}
                y1=${PLOT.top - 8}
                y2=${layout.height}
              ></line>
              <text class="axis" x=${layout.scale(t)} y=${PLOT.top - 12} text-anchor="middle">
                ${this._measure === 'over_hours' ? exact(t) : percent(t)}
              </text>
            </g>`,
          )}
          <line
            class="base-line"
            x1=${layout.baseX}
            x2=${layout.baseX}
            y1=${PLOT.top - 8}
            y2=${layout.height + 4}
          ></line>
          <text class="base-label" x=${layout.baseX} y=${layout.height + 18} text-anchor="middle">
            Base ${this._fmt(model.base.value)}
          </text>
          ${repeat(
            layout.bars,
            (b) => b.key,
            (b) => svg`<g
              class="row"
              data-key=${b.key}
              data-kind=${b.lever.lever_kind}
              data-dim=${Boolean(focus) && focus !== b.key}
            >
              <rect
                class="bar mark"
                role="button"
                tabindex="0"
                data-relieves=${b.relieves ? 'true' : 'false'}
                x=${b.x}
                y=${b.top}
                width=${b.width}
                height=${b.height}
                rx="2"
                @pointerenter=${(e) => {
                  this._hover = b.key;
                  this._showLever(e, model, b.lever);
                }}
                @pointermove=${(e) => this._showLever(e, model, b.lever)}
                @focus=${() => (this._hover = b.key)}
                @blur=${() => (this._hover = null)}
                @click=${() => this._pin(b.key)}
                @keydown=${(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this._pin(b.key);
                  }
                }}
              >
                <title>
                  ${b.lever.label}: ${this._fmt(b.lever.value)},
                  ${signed(b.lever.delta, (v) => this._fmt(v))} from the base
                </title>
              </rect>
              <text class="label" x=${-PLOT.gutter + 8} y=${b.centre} dominant-baseline="middle">
                ${
                  b.lever.label.length > PLOT.label
                    ? `${b.lever.label.slice(0, PLOT.label - 1)}…`
                    : b.lever.label
                }
              </text>
              <text
                class="value"
                x=${b.relieves ? b.x - 6 : b.x + b.width + 6}
                y=${b.centre}
                text-anchor=${b.relieves ? 'end' : 'start'}
                dominant-baseline="middle"
              >${signed(b.lever.delta, (v) => this._fmt(v))}</text>
            </g>`,
          )}
        </g>
      </svg>
    `;
  }

  // -------------------------------------------------------------------------------------------
  // Chrome

  _controls() {
    const group = (options, current, onPick, label) => {
      const slug = label.toLowerCase();
      return html`
        <span class="segmented">
          <span>${label}</span>
          <span class="group" role="group" aria-label=${label}>
            ${options.map(
              ([key, text]) => html`
                <button
                  type="button"
                  data-control=${slug}
                  data-key=${key}
                  aria-pressed=${current === key ? 'true' : 'false'}
                  @click=${() => onPick(key)}
                >
                  ${text}
                </button>
              `,
            )}
          </span>
        </span>
      `;
    };
    return html`
      <div class="controls">
        ${group(
          SENSITIVITY_MEASURES.map((m) => [m, MEASURE_LABELS[m]]),
          this._measure,
          (k) => (this._measure = k),
          'Measure',
        )}
        ${group(
          SENSITIVITY_ORDERS.map((o) => [o, ORDER_LABELS[o]]),
          this._order,
          (k) => (this._order = k),
          'Order',
        )}
        <span class="segmented">
          <span>Show</span>
          <span class="group" role="group" aria-label="Kinds">
            ${LEVER_KINDS.filter((k) => k !== 'base').map(
              (k) => html`
                <button
                  type="button"
                  data-control="kind"
                  data-key=${k}
                  aria-pressed=${this._kinds.includes(k) ? 'true' : 'false'}
                  @click=${() => this._toggleKind(k)}
                >
                  ${LEVER_KIND_LABELS[k]}
                </button>
              `,
            )}
          </span>
        </span>
        <label class="field">
          Hide under
          <select @change=${(e) => (this._floor = Number(e.target.value))}>
            ${FLOORS.map(
              (f) =>
                html`<option value=${f} ?selected=${this._floor === f}>
                  ${f === 0 ? 'nothing' : percent(f)}
                </option>`,
            )}
          </select>
        </label>
        <label class="field">
          Find
          <input
            type="search"
            placeholder="deal or client"
            .value=${this._q}
            @input=${(e) => (this._q = e.target.value)}
          />
        </label>
        ${
          this._pinned
            ? html`<button class="link" type="button" @click=${() => (this._pinned = null)}>
                Clear the isolation
              </button>`
            : nothing
        }
      </div>
    `;
  }

  _toggleKind(kind) {
    const has = this._kinds.includes(kind);
    const next = has ? this._kinds.filter((k) => k !== kind) : [...this._kinds, kind];
    this._kinds = next.length ? next : LEVER_KINDS.filter((k) => k !== 'base');
  }

  _kpis(model, summary) {
    return html`
      <div class="kpis">
        <div class="kpi">
          <div class="n">${this._fmt(model.base.value)}</div>
          <div class="l">peak as it stands</div>
        </div>
        <div class="kpi">
          <div class="n">
            ${
              summary.biggest_relief
                ? signed(summary.biggest_relief.delta, (v) => this._fmt(v))
                : '—'
            }
          </div>
          <div class="l">the most one lever helps</div>
        </div>
        <div class="kpi">
          <div class="n">
            ${summary.biggest_risk ? signed(summary.biggest_risk.delta, (v) => this._fmt(v)) : '—'}
          </div>
          <div class="l">the most one lever hurts</div>
        </div>
        <div class="kpi">
          <div class="n">${count(summary.levers)}</div>
          <div class="l">levers drawn</div>
        </div>
      </div>
    `;
  }

  _legend() {
    return html`
      <div class="legends">
        <ul class="legend">
          <li>
            <span class="sw" style="background: var(--status-healthy)"></span>pulls the peak down
          </li>
          <li><span class="sw" style="background: var(--status-overloaded)"></span>pushes it up</li>
          <li>The base line is the run's own peak — the dashboard's KPI tile.</li>
        </ul>
      </div>
    `;
  }

  _subtitle(model) {
    if (!model) return '';
    const peak = model.base.peak_week_start
      ? `peak week of ${longDate(model.base.peak_week_start)}`
      : 'no peak week';
    return `${peak} · ${longDate(model.from)} to ${longDate(model.to)} · ${plural(
      model.weeks_count,
      'week',
    )}`;
  }

  _notes(model, summary) {
    const notes = [];
    notes.push(
      html`<p class="note">
        Every bar is a scenario you could build. It runs from the run's own peak to the peak that
        lever would produce, so its length is the move and its side is the direction. Each one is
        computed from this run's rows rather than re-forecast: removing a deal removes that deal's
        hours and nothing else, which is exactly what a scenario excluding it would recompute.
      </p>`,
    );

    if (summary.biggest_relief)
      notes.push(
        html`<p class="note" data-note="best">
          The best single lever here is <strong>${summary.biggest_relief.label}</strong>, worth
          ${signed(summary.biggest_relief.delta, (v) => this._fmt(v))} off a peak of
          ${this._fmt(model.base.value)}. Build that scenario below to see what it does to every
          other view.
        </p>`,
      );
    else
      notes.push(
        html`<p class="note" data-note="best">
          Not one lever here takes anything off the peak. The peak is made of committed work, and no
          deal you could lose and no freelancer you could book changes it.
        </p>`,
      );

    if (summary.flat > 0)
      notes.push(
        html`<p class="note" data-note="flat">
          ${plural(summary.flat, 'lever')} move the peak not at all. That is an answer rather than a
          gap: their hours are in weeks that were never the worst one, so losing them would change
          the total and not the problem.
        </p>`,
      );

    notes.push(
      html`<p class="note" data-note="window">
        The window is the run's whole horizon, not the one in the filter bar: a peak that moved when
        you narrowed the window would not be the peak this run is about. The pipeline mode is the
        filter bar's, and it changes which levers exist at all.
      </p>`,
    );

    if (summary.hidden > 0)
      notes.push(
        html`<p class="note" data-note="hidden">
          The controls are hiding ${plural(summary.hidden, 'lever')}. Each lever's own figure is
          about the run and does not follow what is on screen, and the table carries them all.
        </p>`,
      );

    if (model.cached_at)
      notes.push(
        html`<p class="note" data-note="cached">
          Computed once for this run and kept: a run's levers cannot change, so this is the same
          answer rather than an older one.
        </p>`,
      );

    return html`${notes}`;
  }

  get _hourFormats() {
    return {
      byUnit: {
        hours: (v) => (v === null || v === undefined ? '—' : exact(v)),
        percent: (v) => percent(v),
      },
    };
  }

  _table(model) {
    return html`
      <details
        class="table"
        ?open=${this._tableOpen}
        @toggle=${(e) => (this._tableOpen = e.target.open)}
      >
        <summary>Table view</summary>
        <data-table
          caption=${`Every lever against the run's peak — ${this._subtitle(model)}`}
          export-name="sensitivity-levers"
          page-size="25"
          dense
          .columns=${withFormats(sensitivityColumns({ measure: this._measure }), this._hourFormats)}
          .rows=${sensitivityRows(model)}
        ></data-table>
      </details>
    `;
  }

  // -------------------------------------------------------------------------------------------
  // Render

  render() {
    const { run } = this.appState;
    const model = this._model;
    const summary = model ? sensitivitySummary(model) : null;

    let body;
    if (!run) body = html`<div class="state">No forecast run yet — nothing to pull.</div>`;
    else if (this._error) body = html`<div class="state error">${this._error}</div>`;
    else if (!this._data && this._loading)
      body = html`<div class="state">Pulling every lever…</div>`;
    else if (!model) body = html`<div class="state">Nothing to draw.</div>`;
    else if (!model.levers.length)
      body = html`
        ${this._controls()}
        <div class="state" data-note="empty">
          No levers to pull: this run has no open deals and no elastic capacity, so there is nothing
          a single decision could change.
        </div>
        ${this._table(model)}
      `;
    else
      body = html`
        ${this._controls()} ${this._plot(model)} ${this._legend()} ${this._notes(model, summary)}
        ${this._table(model)}
      `;

    return html`
      <div class="card" aria-busy=${this._loading ? 'true' : 'false'}>
        <div class="head">
          <div>
            <div class="eyebrow">Sensitivity</div>
            <h2>Which one decision moves the peak?</h2>
            <div class="sub">${this._subtitle(model)}</div>
          </div>
          ${summary ? this._kpis(model, summary) : nothing}
        </div>
        ${body}
        <chart-tooltip></chart-tooltip>
      </div>
    `;
  }
}

customElements.define('sensitivity-tornado', SensitivityTornado);
